import { useQuery } from "react-query"
import {
  getTicketPurchasesForPost,
  type TicketPurchasesIndexResponse,
} from "../requests/ticketPurchases"

export interface UseTicketPurchasesParams {
  search?: string
  redeemed?: boolean
  page?: number
}

export interface UseTicketPurchasesOptions {
  enabled?: boolean
  refetchInterval?: number
  staleTime?: number
  cacheTime?: number
}

/**
 * Hook to fetch ticket purchases for a specific post (band management view)
 *
 * @param postId - The ID of the post to get tickets for
 * @param params - Optional search and filter parameters
 * @param options - React Query options
 * @returns Query result with ticket purchases data
 */
export function useTicketPurchasesForPost(
  postId: number | null,
  params: UseTicketPurchasesParams = {},
  options: UseTicketPurchasesOptions = {},
) {
  const { search, redeemed, page } = params
  const { enabled = true, refetchInterval, staleTime, cacheTime } = options

  return useQuery<TicketPurchasesIndexResponse, Error>(
    ["ticketPurchases", postId, search, redeemed, page],
    () => getTicketPurchasesForPost(postId!, params),
    {
      enabled: enabled && postId !== null,
      refetchInterval,
      staleTime: staleTime || 5 * 60 * 1000, // 5 minutes
      cacheTime: cacheTime || 10 * 60 * 1000, // 10 minutes
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  )
}

/**
 * Hook to get ticket purchases with search functionality
 *
 * @param postId - The ID of the post to get tickets for
 * @param searchTerm - Search term to filter tickets by user name
 * @param options - React Query options
 * @returns Query result with filtered ticket purchases
 */
export function useTicketPurchasesSearch(
  postId: number | null,
  searchTerm: string = "",
  options: UseTicketPurchasesOptions = {},
) {
  return useTicketPurchasesForPost(
    postId,
    { search: searchTerm || undefined },
    options,
  )
}

/**
 * Hook to get ticket purchases filtered by redemption status
 *
 * @param postId - The ID of the post to get tickets for
 * @param redeemedOnly - Whether to show only redeemed tickets
 * @param options - React Query options
 * @returns Query result with filtered ticket purchases
 */
export function useTicketPurchasesByStatus(
  postId: number | null,
  redeemedOnly: boolean | null = null,
  options: UseTicketPurchasesOptions = {},
) {
  return useTicketPurchasesForPost(
    postId,
    { redeemed: redeemedOnly || undefined },
    options,
  )
}

/**
 * Hook to get only redeemed tickets
 *
 * @param postId - The ID of the post to get tickets for
 * @param options - React Query options
 * @returns Query result with redeemed tickets only
 */
export function useRedeemedTickets(
  postId: number | null,
  options: UseTicketPurchasesOptions = {},
) {
  return useTicketPurchasesByStatus(postId, true, options)
}

/**
 * Hook to get only unredeemed tickets
 *
 * @param postId - The ID of the post to get tickets for
 * @param options - React Query options
 * @returns Query result with unredeemed tickets only
 */
export function useUnredeemedTickets(
  postId: number | null,
  options: UseTicketPurchasesOptions = {},
) {
  return useTicketPurchasesByStatus(postId, false, options)
}

// Query key factory for manual cache management
export const ticketPurchasesQueryKeys = {
  all: ["ticketPurchases"] as const,
  byPost: (postId: number) => ["ticketPurchases", postId] as const,
  byPostAndParams: (postId: number, params: UseTicketPurchasesParams) =>
    ["ticketPurchases", postId, params.search, params.redeemed] as const,
}
